import { BaseMenu } from '@components/BaseMenu';
import { isLibraryVerbId } from '@lib/verbs';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Divider, IconButton, MenuItem } from '@mui/material';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { MouseEvent, RefObject, useMemo, useState } from 'react';
import { LibraryDeleteModalRef } from './LibraryDeleteModal';
import { LIBRARY_MENU_ACTIONS, LibraryMenuAction } from './libraryMenuActions';
import css from './LibraryMenuButton.module.scss';
import { LibraryRenameModalRef } from './LibraryRenameModal';

interface LibraryMenuButtonProps {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  disableDelete?: boolean;
  renameModalRef: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef: RefObject<LibraryDeleteModalRef | null>;
  /**
   * `default` is card-shaped, with a corner radius cut for the library card's
   * top-right. `in-library` is the flatter page-header form. `row` is a circular
   * 40px control for list rows.
   */
  variant?: 'default' | 'in-library' | 'row';
  libraryId?: string;
}

export const LibraryMenuButton = ({
  onClick,
  disableDelete,
  renameModalRef,
  deleteModalRef,
  variant = 'default',
  libraryId,
}: LibraryMenuButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    if (libraryId) trackingStore.libraryMenuOpened({ libraryId });
    onClick?.(event);
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (libraryId) trackingStore.libraryMenuClosed({ libraryId });
  };

  const handleAction = (action: LibraryMenuAction, event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (libraryId) trackingStore.libraryMenuAction({ action, libraryId });

    switch (action) {
      case 'rename':
        renameModalRef.current?.open();
        break;
      case 'delete':
        deleteModalRef.current?.open();
        break;
    }
  };

  // This kebab never carries the verbs: every surface that mounts it already shows them
  // alongside. On the Libraries page that's the card's hover rail and the table row's
  // rail; in a library it's `LibraryActionRow`, sitting in the same header a few pixels
  // away. So the menu is purely "Manage library", which is what its own title says.
  //
  // Consequence worth knowing: `LibraryActionRow` hides itself on an empty library
  // (`stats?.count === 0`), so there the verbs disappear from the desktop entirely. That
  // matches `buildLibraryRailItems`' own `showVerbs = !isEmpty` rule — a chat about no
  // media and a search over no media are both dead ends — so it's the intended answer
  // rather than a gap.
  //
  // `MobileLibraryContextDrawer` deliberately does NOT do this — mobile list view has no
  // rail, so the drawer is the only way in there. See the note on its `actions`. Mobile
  // never renders THIS component: `LibraryContent` branches to `MobileLibraryHeader`,
  // and `LibraryContentHeaderLeft` (which owns this kebab) is desktop-only.
  const actions = useMemo(() => {
    // Via `LIBRARY_VERB_IDS` rather than naming the two ids, so adding a third library verb
    // can't silently leave it duplicated here. Same shape `VideoMenuButton` uses for the media
    // set.
    let items = LIBRARY_MENU_ACTIONS.filter((a) => !isLibraryVerbId(a.id));
    if (disableDelete) items = items.filter((a) => a.id !== 'delete');
    return items;
  }, [disableDelete]);

  return (
    <>
      <IconButton
        className={classNames(
          css.threeDots,
          variant === 'in-library' && css.inLibraryThreeDots,
          variant === 'row' && css.rowThreeDots,
        )}
        onClick={handleClick}
        aria-label="library menu"
        aria-controls={open ? 'library-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreHorizIcon />
      </IconButton>
      <BaseMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <span className={css.menuTitle}>Manage library</span>
        <Divider />
        {actions.map((action) => (
          <MenuItem key={action.id} onClick={(e) => handleAction(action.id, e)}>
            <span className={classNames(css.menuItemContent, action.danger && css.dangerItem)}>
              <span className={css.menuIcon}>{action.icon}</span>
              {action.label}
            </span>
          </MenuItem>
        ))}
      </BaseMenu>
    </>
  );
};
